import PropertyPanel from '../widgets/PropertyPanel.js';

/**
 * GradientPropertyPanel - UI for gradient material properties
 */
export default class GradientPropertyPanel extends PropertyPanel {
  render(initialData = {}) {
    this.clear();

    // Gradient type
    const typeSelect = this.addSelect('Type', [
      { value: 'linear', label: 'Linear' },
      { value: 'radial', label: 'Radial' }
    ], initialData.gradientType || 'linear');
    typeSelect.addEventListener('change', () => {
      if (this.onChange) this.onChange();
    });
    this.controls.typeSelect = typeSelect;

    // Color 1
    const color1 = this.addColorPicker('Color 1', initialData.color1 || '#3498db');
    color1.addEventListener('input', () => {
      if (this.onChange) this.onChange();
    });
    this.controls.color1 = color1;

    // Color 2
    const color2 = this.addColorPicker('Color 2', initialData.color2 || '#e74c3c');
    color2.addEventListener('input', () => {
      if (this.onChange) this.onChange();
    });
    this.controls.color2 = color2;

    // Gradient axis selector
    const axisSelect = this.addSelect('Gradient Axis', [
      { value: 'y', label: 'Y (Vertical - Up/Down)' },
      { value: 'x', label: 'X (Horizontal - Left/Right)' },
      { value: 'z', label: 'Z (Depth - Front/Back)' }
    ], initialData.axis || 'y');
    axisSelect.addEventListener('change', () => {
      this.updateOffsetVisibility();
      if (this.onChange) this.onChange();
    });
    this.controls.axisSelect = axisSelect;

    // XYZ Scale controls
    const scaleSection = this.builder.createAnimatedSubsection('Scale', 1, false);
    const xyzControl = this.builder.createXYZControl(
      (axis, value, isLinked) => {
        if (this.onChange) this.onChange();
      },
      (linked) => {
        if (this.onChange) this.onChange();
      }
    );
    scaleSection.body.appendChild(xyzControl.control);
    this.container.appendChild(scaleSection.subsection);
    this.controls.scaleControl = xyzControl;

    // Offset controls - only show relevant one based on axis
    const offsetXSlider = this.addSlider('Offset', -100, 100, 1, initialData.offsetX || 0, (val) => {
      if (this.onChange) this.onChange();
    });
    this.controls.offsetXSlider = offsetXSlider;

    const offsetYSlider = this.addSlider('Offset', -100, 100, 1, initialData.offsetY || 0, (val) => {
      if (this.onChange) this.onChange();
    });
    this.controls.offsetYSlider = offsetYSlider;

    const offsetZSlider = this.addSlider('Offset', -100, 100, 1, initialData.offsetZ || 0, (val) => {
      if (this.onChange) this.onChange();
    });
    this.controls.offsetZSlider = offsetZSlider;

    // Show only relevant offset based on initial axis
    this.updateOffsetVisibility();

    // Opacity
    const opacitySlider = this.addSlider('Opacity', 0, 1, 0.01, initialData.opacity || 1, (val) => {
      if (this.onOpacityChange) this.onOpacityChange(val);
      if (this.onChange) this.onChange();
    });
    this.controls.opacitySlider = opacitySlider;
  }

  /**
   * Show only the offset control relevant to the current axis
   */
  updateOffsetVisibility() {
    const axis = this.controls.axisSelect.value;

    // Hide all offset containers
    if (this.controls.offsetXSlider.container) {
      this.controls.offsetXSlider.container.style.display = axis === 'x' ? 'block' : 'none';
    }
    if (this.controls.offsetYSlider.container) {
      this.controls.offsetYSlider.container.style.display = axis === 'y' ? 'block' : 'none';
    }
    if (this.controls.offsetZSlider.container) {
      this.controls.offsetZSlider.container.style.display = axis === 'z' ? 'block' : 'none';
    }
  }

  getData() {
    const axis = this.controls.axisSelect.value;
    return {
      type: 'gradient',
      gradientType: this.controls.typeSelect.value,
      color1: this.controls.color1.value,
      color2: this.controls.color2.value,
      axis: axis,
      angle: 0, // Rotation disabled
      scaleX: parseFloat(this.controls.scaleControl.controls.x.slider.value),
      scaleY: parseFloat(this.controls.scaleControl.controls.y.slider.value),
      scaleZ: parseFloat(this.controls.scaleControl.controls.z.slider.value),
      // Only send the relevant offset based on axis
      offsetX: axis === 'x' ? parseFloat(this.controls.offsetXSlider.slider.value) : 0,
      offsetY: axis === 'y' ? parseFloat(this.controls.offsetYSlider.slider.value) : 0,
      offsetZ: axis === 'z' ? parseFloat(this.controls.offsetZSlider.slider.value) : 0,
      opacity: parseFloat(this.controls.opacitySlider.slider.value)
    };
  }

  setData(data) {
    if (data.gradientType) this.controls.typeSelect.value = data.gradientType;
    if (data.color1) this.controls.color1.value = data.color1;
    if (data.color2) this.controls.color2.value = data.color2;
    if (data.axis) this.controls.axisSelect.value = data.axis;
    if (data.scaleX !== undefined) {
      this.controls.scaleControl.controls.x.slider.value = data.scaleX;
      this.controls.scaleControl.controls.x.valueInput.value = data.scaleX;
    }
    if (data.scaleY !== undefined) {
      this.controls.scaleControl.controls.y.slider.value = data.scaleY;
      this.controls.scaleControl.controls.y.valueInput.value = data.scaleY;
    }
    if (data.scaleZ !== undefined) {
      this.controls.scaleControl.controls.z.slider.value = data.scaleZ;
      this.controls.scaleControl.controls.z.valueInput.value = data.scaleZ;
    }
    if (data.offsetX !== undefined) {
      this.controls.offsetXSlider.slider.value = data.offsetX;
      this.controls.offsetXSlider.valueInput.value = data.offsetX;
    }
    if (data.offsetY !== undefined) {
      this.controls.offsetYSlider.slider.value = data.offsetY;
      this.controls.offsetYSlider.valueInput.value = data.offsetY;
    }
    if (data.offsetZ !== undefined) {
      this.controls.offsetZSlider.slider.value = data.offsetZ;
      this.controls.offsetZSlider.valueInput.value = data.offsetZ;
    }
    if (data.opacity !== undefined) {
      this.controls.opacitySlider.slider.value = data.opacity;
      this.controls.opacitySlider.valueInput.value = data.opacity;
    }
    // Update offset visibility based on axis
    this.updateOffsetVisibility();
  }
}
